import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { resolvePreset, previousPeriod, type DateRange, type PresetId } from "../lib/date-range";

/**
 * Estado compartido del filtro por fechas (Fase FF1). Un único selector global cuyo rango elegido usan
 * el Dashboard y las secciones. Recuerda la elección entre sesiones (localStorage). Expone el rango
 * actual, el período anterior equivalente (para comparar) y el preset activo.
 */
interface DateRangeState {
  preset: PresetId;
  range: DateRange;
  previous: DateRange;
  compare: boolean;
  setPreset: (p: PresetId) => void;
  setCustom: (range: DateRange) => void;
  setCompare: (on: boolean) => void;
}

const KEY = "fabianmeli.dateFilter";
const Ctx = createContext<DateRangeState | null>(null);

interface Persisted { preset: PresetId; custom?: DateRange | undefined; compare: boolean }
function load(): Persisted {
  try { const raw = localStorage.getItem(KEY); if (raw) return JSON.parse(raw) as Persisted; } catch { /* ignore */ }
  return { preset: "last30", compare: true };
}

export function DateRangeProvider({ children }: { children: ReactNode }): JSX.Element {
  const initial = load();
  const [preset, setPresetState] = useState<PresetId>(initial.preset);
  const [custom, setCustomState] = useState<DateRange | undefined>(initial.custom);
  const [compare, setCompareState] = useState<boolean>(initial.compare);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify({ preset, custom, compare } satisfies Persisted)); } catch { /* ignore */ }
  }, [preset, custom, compare]);

  const value = useMemo<DateRangeState>(() => {
    const range = resolvePreset(preset, Date.now(), custom);
    return {
      preset, range, previous: previousPeriod(range, preset), compare,
      setPreset: (p) => setPresetState(p),
      setCustom: (r) => { setCustomState(r); setPresetState("custom"); },
      setCompare: (on) => setCompareState(on),
    };
  }, [preset, custom, compare]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Hook para leer/controlar el filtro de fechas global. */
export function useDateRange(): DateRangeState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useDateRange debe usarse dentro de <DateRangeProvider>");
  return v;
}
